import { Alert, Box, Stack, Typography } from "@mui/material";
import { type BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";

import { verdictText } from "./DataSourceConflictsPane.helpers";
import { type DiscrepancyStatus, hasResolvableAction } from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyRowExpanderShell } from "./DiscrepancyRowExpanderShell";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/**
 * One "does this source differ" verdict. Label and answer stay in one text node
 * so the line reads as a sentence.
 */
function VerdictLine({ label, verdict }: { label: string; verdict: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{" "}
      {verdict}
    </Typography>
  );
}

export function DataSourceConflictRowExpander({
  discrepancy,
  status,
  open,
  colSpan,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
}: {
  discrepancy: Discrepancy;
  status: DiscrepancyStatus;
  open: boolean;
  colSpan: number;
  /** Every row action here is staff-only; the row still renders values, verdicts, and history for a customer. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  mutationLoading?: boolean;
}) {
  return (
    <DiscrepancyRowExpanderShell
      discrepancy={discrepancy}
      status={status}
      open={open}
      colSpan={colSpan}
      isStaff={isStaff}
      canResolve={hasResolvableAction(discrepancy, true)}
      isMutating={mutationLoading === true}
      onResolve={() => onResolve(discrepancy)}
      onMarkUnresolved={() => onMarkUnresolved(discrepancy)}
      onUndoResolution={() => onUndoResolution(discrepancy)}
    >
      {/* The source values already show in the row above, so only the verdicts repeat here. */}
      <Box sx={{ width: "fit-content", maxWidth: "100%" }}>
        <Stack spacing={0.25}>
          <VerdictLine label="Vendor differs" verdict={verdictText(discrepancy.vendorDiscrepant)} />
          <VerdictLine
            label="Document differs"
            verdict={verdictText(discrepancy.documentDiscrepant)}
          />
        </Stack>

        {discrepancy.vendorAlignmentSuspect === true && (
          <Alert severity="warning" sx={{ py: 0 }}>
            The vendor and e-Manifest copies list a different number of waste lines, so the lines
            cannot be paired by number. Use Match waste lines in the warning above these findings to
            say which vendor line is which e-Manifest line.
          </Alert>
        )}
      </Box>
    </DiscrepancyRowExpanderShell>
  );
}
